import React, { useEffect, useState, useMemo } from "react";
import {
  Row, Col, Card, CardBody, CardTitle, Button, Modal, ModalHeader, ModalBody, ModalFooter,
  Form, FormGroup, Label, Input, Spinner, Alert
} from "reactstrap";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { pb } from "../../../helpers/pocketbase_helper";
import TableContainer from "../../../components/Common/DataTableContainer";
import { formatProductoNombre } from "../../../utils/productoInventarioHelper";

const InventarioSalidas = () => {
  document.title = "Salidas de Inventario | GraphicForce - Printesco";

  // Data states
  const [inventarios, setInventarios] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);

  // UI states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Pagination states
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  // Modal states
  const [modalRestar, setModalRestar] = useState(false);
  const [form, setForm] = useState({ productoInventario: "", cantidad: 0 });
  const [selectedProductoInfo, setSelectedProductoInfo] = useState(null);
  const [saving, setSaving] = useState(false);

  // Fetch inventory data
  const fetchData = async () => {
    setLoading(true);
    setError("");
    try {
      // Fetch only active inventory records with total > 0
      const resultList = await pb.collection("producto_inventario").getList(1, 1000, {
        filter: "activo = true && total > 0",
        expand: "tinta",
        sort: "nombre"
      });

      // Apply pagination
      const startIndex = pageIndex * pageSize;
      const endIndex = startIndex + pageSize;
      const paginatedItems = resultList.items.slice(startIndex, endIndex);

      setInventarios(paginatedItems);
      setTotalRecords(resultList.items.length);
    } catch (err) {
      console.error("Error fetching data:", err);
      setError("Error al cargar los datos: " + err.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [pageIndex, pageSize]);

  // Column definitions
  const columns = useMemo(() => [
    {
      header: "Nombre",
      accessorKey: "nombre",
      enableColumnFilter: false,
      enableSorting: true,
      cell: ({ row }) => {
        const producto = row.original;
        return formatProductoNombre(producto);
      },
    },
    {
      header: "Medida",
      accessorKey: "texto_medida",
      enableColumnFilter: false,
      enableSorting: true,
      cell: (info) => info.getValue() || "-",
    },
    {
      header: "Disponible",
      accessorKey: "total",
      enableColumnFilter: false,
      enableSorting: true,
      cell: (info) => info.getValue() || 0,
    },
    {
      header: "Última Actualización",
      accessorKey: "updated",
      enableColumnFilter: false,
      enableSorting: true,
      cell: (info) => {
        const date = new Date(info.getValue());
        return date.toLocaleDateString("es-ES");
      },
    },
    {
      header: "Acciones",
      accessorKey: "acciones",
      cell: (info) => {
        const record = info.row.original;
        return (
          <Button
            color="warning"
            size="sm"
            onClick={() => handleOpenRestar(record)}
          >
            <i className="fas fa-minus me-1"></i> Restar
          </Button>
        );
      },
      enableColumnFilter: false,
      enableSorting: false,
      meta: { style: { width: "1%", whiteSpace: "nowrap" } },
    },
  ], []);

  // Handlers
  const handleOpenRestar = (record) => {
    setSelectedProductoInfo(record);
    setForm({ productoInventario: record.id, cantidad: 0 });
    setError("");
    setSuccess("");
    setModalRestar(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "cantidad") {
      setForm({ ...form, [name]: parseFloat(value) || 0 });
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");

    if (!form.productoInventario || form.cantidad <= 0) {
      setError("Seleccione un producto de inventario e ingrese una cantidad válida");
      setSaving(false);
      return;
    }

    try {
      // Find the inventory record for this producto_inventario
      const inventoryRecord = inventarios.find(
        inv => inv.id === form.productoInventario
      );

      if (!inventoryRecord) {
        setError("Producto de inventario no encontrado");
        setSaving(false);
        return;
      }

      // Validate that quantity doesn't exceed available
      if (form.cantidad > inventoryRecord.total) {
        setError(`No hay suficiente cantidad disponible. Disponible: ${inventoryRecord.total}, Solicitado: ${form.cantidad}`);
        setSaving(false);
        return;
      }

      // Update the inventory record - decrement total
      const newTotal = inventoryRecord.total - form.cantidad;
      const updateData = {
        total: newTotal,
        activo: newTotal > 0 // If total reaches 0, can mark as inactive or keep active
      };

      await pb.collection("producto_inventario").update(inventoryRecord.id, updateData);

      setSuccess("Salida registrada exitosamente");
      setModalRestar(false);
      setPageIndex(0);
      fetchData();
    } catch (err) {
      console.error("Error saving exit:", err);
      setError("Error al registrar la salida: " + (err.message || "Error desconocido"));
    }
    setSaving(false);
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs maintitle="Dashboard" title="Inventarios" breadcrumbItem="Salidas" />

          {/* Success Alert */}
          {success && (
            <Alert color="success" isOpen={!!success} toggle={() => setSuccess("")} fade={true}>
              <strong>¡Éxito!</strong> {success}
            </Alert>
          )}

          {/* Error Alert */}
          {error && (
            <Alert color="danger" isOpen={!!error} toggle={() => setError("")} fade={true}>
              <strong>Error:</strong> {error}
            </Alert>
          )}

          <Row>
            <Col>
              <Card>
                <CardBody>
                  <CardTitle className="h4 d-flex justify-content-between align-items-center">
                    Salidas de Inventario
                    <div>
                      <Button
                        color="secondary"
                        size="sm"
                        title="Recargar tabla"
                        onClick={fetchData}
                      >
                        <i className="fas fa-sync-alt"></i>
                      </Button>
                    </div>
                  </CardTitle>
                  <div className="table-rep-plugin" style={{ overflow: "visible" }}>
                    <div className="table-responsive mb-0" data-pattern="priority-columns" style={{ overflow: "visible" }}>
                      {loading ? (
                        <div className="text-center p-4">
                          <Spinner color="primary" />
                        </div>
                      ) : (
                        <TableContainer
                          columns={columns}
                          data={inventarios}
                          isGlobalFilter={false}
                          isPagination={true}
                          isCustomPageSize={true}
                          pageIndex={pageIndex}
                          setPageIndex={setPageIndex}
                          pageSize={pageSize}
                          setPageSize={setPageSize}
                          totalRecords={totalRecords}
                          pagination="pagination"
                          paginationWrapper='dataTables_paginate paging_simple_numbers'
                          tableClass="table-bordered table-nowrap dt-responsive w-100 dataTable no-footer dtr-inline"
                        />
                      )}
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>

      {/* Record Exit Modal */}
      <Modal isOpen={modalRestar} toggle={() => setModalRestar(!modalRestar)} size="lg">
        <ModalHeader toggle={() => setModalRestar(!modalRestar)}>
          Registrar Salida
        </ModalHeader>
        <Form onSubmit={handleSave}>
          <ModalBody>
            {error && (
              <Alert color="danger" isOpen={!!error} className="mb-3">
                <strong>Error:</strong> {error}
              </Alert>
            )}
            <FormGroup>
              <Label for="productoInventario">Producto de Inventario *</Label>
              <Input
                type="select"
                name="productoInventario"
                id="productoInventario"
                value={form.productoInventario}
                onChange={handleChange}
                required
              >
                <option value="">Seleccione un producto de inventario</option>
                {inventarios.map(producto => {
                  const nombreFormateado = formatProductoNombre(producto);
                  return (
                    <option key={producto.id} value={producto.id}>
                      {nombreFormateado} - Disponible: {producto.total || 0}
                    </option>
                  );
                })}
              </Input>
            </FormGroup>
            {form.productoInventario && (
              <FormGroup>
                <Label>
                  Disponible: {inventarios.find(
                    inv => inv.id === form.productoInventario
                  )?.total || 0}
                </Label>
              </FormGroup>
            )}
            <FormGroup>
              <Label for="cantidad">Cantidad a Restar *</Label>
              <Input
                type="number"
                name="cantidad"
                id="cantidad"
                value={form.cantidad}
                onChange={handleChange}
                placeholder="Ingrese la cantidad a restar"
                step="0.01"
                min="0"
                required
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setModalRestar(false)}>
              Cancelar
            </Button>
            <Button color="primary" type="submit" disabled={saving}>
              {saving ? <Spinner size="sm" className="me-2" /> : null}
              {saving ? "Guardando..." : "Restar"}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    </React.Fragment>
  );
};

export default InventarioSalidas;
